import React, { useState } from 'react';
import '../../App.scss';
import 'antd/dist/antd.css';
import {
  Button, Grid, Input, Modal, Popover, Radio, Space, Switch, Table, Tag as AntTag, Typography, message, Tooltip,
} from 'antd';
import {
  CheckOutlined, EditOutlined, ExclamationOutlined, FilePdfOutlined,
  LinkOutlined, PauseCircleOutlined, PlayCircleOutlined, QrcodeOutlined, ShareAltOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import { deviceCategoryKey, DEVICE_CATEGORIES, deviceKindLabel } from '../../utils/deviceCategory';
import { shareUrl } from '../../utils/URLBuilder';

import DeviceImage from './DeviceImage';

const { Text } = Typography;
const { useBreakpoint } = Grid;

/** One vocabulary for the kind, table and compact line alike. */
const kindLabelFor = (tag) => (tag.deviceKind
  ? deviceKindLabel(tag.deviceKind)
  : (DEVICE_CATEGORIES[deviceCategoryKey(tag)]?.label || tag.product || '—'));

/** {money, taps} for LIVE tags with history; null otherwise. */
const liveTotalsFor = (tag) => {
  if (tag.status !== 'live' || (tag.totalNetValue === undefined && tag.countOfTaps === undefined)) {
    return null;
  }
  const money = new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: tag.paymentDetails?.currency || 'EUR',
  }).format((tag.totalNetValue || 0) / 100);
  return { money, taps: tag.countOfTaps || 0 };
};

/** Same identity grammar as the Smart POS wall and Ops → Terminals. */
const shortId = (tag) => {
  const isTerminal = String(tag.tagUid || '').startsWith('tmr_');
  if (isTerminal) {
    return tag.deviceSerial
      ? `SN ${String(tag.deviceSerial).slice(-4).toUpperCase()}`
      : String(tag.tagUid || tag.id || '');
  }
  const id = String(tag.tagUid || tag.id || '');
  return id.length > 15 ? `QR ${id.slice(0, 4)}—${id.slice(-4)}` : id;
};

const lozenge = (text) => (
  <span style={{
    display: 'inline-block',
    padding: '1px 8px',
    borderRadius: '10px',
    background: '#F4EEE2',
    border: '1px solid #E3D9C2',
    color: '#6B5D42',
    fontSize: '11px',
    whiteSpace: 'nowrap',
  }}
  >
    {text}
  </span>
);

/** Physical forms a QR fixture can join the Smart POS wall as (terminals
 * join via claim, never this picker). */
const DEVICE_KIND_CHOICES = [
  ['stand', 'Smart Stand'],
  ['sticker', 'Smart Sticker'],
  ['card', 'Smart Card'],
  ['fob', 'Smart Fob'],
];

const qrPngUrl = (tag) => {
  const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:8088' : 'https://pay.strikepay.co';
  const tagId = (tag.templateTagId && tag.tagUid) ? `${tag.templateTagId}---${tag.tagUid}` : tag.id;
  return `${baseUrl}/qr/${tagId}/png`;
};

/**
 * ONE flat, scannable list of a team's tags and devices — replaces the
 * three-section card grid (Charles, 2026-08-27: "horrible and wasteful").
 * QR codes live behind a per-row popover; names edit inline; the Smart POS
 * switch controls whether the tag appears on the operator's device wall.
 * Row drill-in (Edit) shows the rest of the metadata.
 */
export const StrikeTagsListView = ({
  data, isAdmin, onEditTag, team, match,
}) => {
  const [query, setQuery] = useState('');
  const screens = useBreakpoint();
  // Below md the Kind/Status/Total columns fold into the name cell and the
  // action buttons go icon-only. Only an EXPLICIT false is compact — the
  // hook returns {} on the first render tick, and that must stay desktop.
  const compact = screens.md === false;
  const [busyTagId, setBusyTagId] = useState(null);
  // Toggling a tag to "smart" asks WHAT it physically is first.
  const [kindPicker, setKindPicker] = useState(null); // { tag, kind }

  const isTerminalLike = (tag) => {
    const key = deviceCategoryKey(tag);
    return key === 'tipping-terminal' || key === 'paytip-terminal' || key === 'handoff-terminal';
  };

  // The disabled switch tells the TRUTH per family: Stripe terminals are
  // ALWAYS on the wall; third-party terminals (pax/corevend/ecomm) NEVER
  // join it — the hand-off machinery is Stripe-only (Charles, 2026-10-04).
  const smartPosTooltip = (tag) => {
    const key = deviceCategoryKey(tag);
    if (key === 'handoff-terminal') return 'Stripe Terminals are always Smart POS devices';
    if (key === 'tipping-terminal' || key === 'paytip-terminal') return 'Third-party terminals can\u2019t be driven from the Smart POS';
    return 'Show on the Smart POS device wall';
  };

  const saveNickname = async (tag, nickname) => {
    const trimmed = String(nickname || '').trim();
    if (trimmed === String(tag.metadata?.nickname || '')) return;
    setBusyTagId(tag.id);
    try {
      await onEditTag(tag, { metadata: { ...(tag.metadata || {}), nickname: trimmed } });
      message.success(trimmed ? `Named "${trimmed}"` : 'Name cleared');
    } catch (err) {
      message.error('Could not save the name');
    } finally {
      setBusyTagId(null);
    }
  };

  const setSmartDevice = async (tag, deviceKind) => {
    setBusyTagId(tag.id);
    try {
      // A truthy deviceKind puts the tag on the Smart POS device wall;
      // null takes it off. Terminals are locked ON (the reader IS a device).
      await onEditTag(tag, { deviceKind });
      message.success(deviceKind
        ? `Available to Smart POS as a ${deviceKind}` : 'Removed from Smart POS');
    } catch (err) {
      message.error('Could not change Smart POS availability');
    } finally {
      setBusyTagId(null);
    }
  };

  const toggleSmartDevice = (tag, on) => {
    if (!on) { setSmartDevice(tag, null); return; }
    // ON asks what the physical fixture IS before it joins the wall.
    setKindPicker({ tag, kind: tag.deviceKind || 'stand' });
  };

  const togglePause = async (tag) => {
    const newStatus = tag.status === 'live' ? 'paused' : 'live';
    setBusyTagId(tag.id);
    try {
      await onEditTag(tag, { status: newStatus });
      message.success(`Tag ${newStatus === 'live' ? 'enabled' : 'paused'}`);
    } catch (err) {
      message.error('Could not change status');
    } finally {
      setBusyTagId(null);
    }
  };

  const qrPopover = (tag) => (
    <div style={{ textAlign: 'center', maxWidth: '240px' }}>
      <img src={qrPngUrl(tag)} alt="QR" style={{ width: '200px', height: '200px' }} />
      <div style={{ marginTop: '4px' }}>{lozenge(shortId(tag))}</div>
      <Space style={{ marginTop: '8px' }}>
        <Button
          size="small"
          icon={<ShareAltOutlined />}
          onClick={() => {
            const url = shareUrl(tag, null);
            if (navigator.share) navigator.share({ title: tag.metadata?.nickname || 'Strikepay', url }).catch(() => {});
            else window.open(url);
          }}
        >
          Share
        </Button>
        <CopyToClipboard text={shareUrl(tag)} onCopy={() => message.success('Link copied')}>
          <Button size="small" icon={<LinkOutlined />}>Copy</Button>
        </CopyToClipboard>
        {!isTerminalLike(tag) && (
          <a href={qrPngUrl(tag)} target="_blank" rel="noopener noreferrer">
            <Button size="small" icon={<FilePdfOutlined />}>PNG</Button>
          </a>
        )}
      </Space>
    </div>
  );

  const filtered = (data || []).filter((tag) => {
    if (!query.trim()) return true;
    const hay = [tag.metadata?.nickname, tag.id, tag.tagUid, tag.templateTagId,
      tag.deviceKind, tag.deviceSerial].filter(Boolean).join(' ').toLowerCase();
    return hay.includes(query.trim().toLowerCase());
  });
  // STABLE alphabetical order by name-or-id — an edit (which bumps
  // updatedAt) must never move a row under the operator's cursor.
  const sortKey = (t) => String(t.metadata?.nickname || t.tagUid || t.id || '').toLowerCase();
  const rows = [...filtered].sort((a, b) => sortKey(a).localeCompare(sortKey(b)));

  const columns = [
    {
      title: 'Device / tag',
      key: 'name',
      render: (tag) => (
        <Space>
          <DeviceImage
            tag={tag}
            imgStyle={{ width: '36px', height: '36px', objectFit: 'contain', borderRadius: '6px' }}
            iconStyle={{ fontSize: '24px', color: '#999' }}
          />
          <Space direction="vertical" size={0}>
            <Text
              editable={{
                onChange: (value) => saveNickname(tag, value),
                tooltip: 'Name this device',
              }}
              style={!tag.metadata?.nickname ? { fontStyle: 'italic', color: '#999' } : {}}
            >
              {tag.metadata?.nickname || 'Unnamed'}
            </Text>
            {lozenge(shortId(tag))}
            {compact && (
              <Text type="secondary" style={{ fontSize: '11px' }}>
                {[kindLabelFor(tag), tag.status || '—',
                  liveTotalsFor(tag) && `${liveTotalsFor(tag).money} · ${liveTotalsFor(tag).taps} taps`,
                ].filter(Boolean).join(' · ')}
              </Text>
            )}
          </Space>
        </Space>
      ),
    },
    {
      title: 'Kind',
      key: 'kind',
      responsive: ['md'],
      render: (tag) => (
        <Space direction="vertical" size={0}>
          {/* The DEVICE KIND leads — the same word the Smart POS card header
              uses (Terminal, QR Stand, Sticker, …); plain QRs show their
              category. Template rides beneath. */}
          <Text>{kindLabelFor(tag)}</Text>
          <Text type="secondary" style={{ fontSize: '11px' }}>{tag.templateTagId || tag.id}</Text>
        </Space>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      responsive: ['md'],
      render: (tag) => (
        <AntTag color={tag.status === 'live' ? 'green' : 'default'}>
          {tag.status === 'live' ? <CheckOutlined /> : <ExclamationOutlined />}
          {tag.is_test ? '*' : ''}
          {` ${tag.status || '—'}`}
        </AntTag>
      ),
    },
    {
      title: 'Total',
      key: 'total',
      responsive: ['md'],
      render: (tag) => {
        // Running totals for LIVE tags only — a paused tag's history lives
        // in its drill-in, not the working list.
        const totals = liveTotalsFor(tag);
        if (!totals) {
          return <Text type="secondary">—</Text>;
        }
        return (
          <Space direction="vertical" size={0} style={{ fontVariantNumeric: 'tabular-nums' }}>
            <Text strong style={{ color: '#237804' }}>{totals.money}</Text>
            <Text type="secondary" style={{ fontSize: '11px' }}>{`${totals.taps} taps`}</Text>
          </Space>
        );
      },
    },
    {
      title: 'Smart POS',
      key: 'smart',
      render: (tag) => (
        <Tooltip title={smartPosTooltip(tag)}>
          <Switch
            size="small"
            checked={!!tag.deviceKind}
            disabled={isTerminalLike(tag)}
            loading={busyTagId === tag.id}
            onChange={(on) => toggleSmartDevice(tag, on)}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (tag) => (
        <Space>
          <Popover content={qrPopover(tag)} trigger="click" destroyTooltipOnHide>
            <Button size="small" icon={<QrcodeOutlined />}>{compact ? null : 'QR'}</Button>
          </Popover>
          <Link to={{ pathname: `${match.url}/${tag.id}`, state: { fromTagsList: true } }}>
            <Button size="small" icon={<EditOutlined />}>{compact ? null : 'Edit'}</Button>
          </Link>
          {isAdmin && typeof onEditTag === 'function' && (
            <Tooltip title={compact ? (tag.status === 'live' ? 'Pause' : 'Unpause') : ''}>
              <Button
                size="small"
                danger={tag.status === 'live'}
                loading={busyTagId === tag.id}
                icon={compact ? (tag.status === 'live' ? <PauseCircleOutlined /> : <PlayCircleOutlined />) : null}
                onClick={() => togglePause(tag)}
              >
                {compact ? null : (tag.status === 'live' ? 'Pause' : 'Unpause')}
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ margin: '12px' }}>
      <Modal
        title={`What kind of device is ${kindPicker?.tag?.metadata?.nickname || 'this tag'}?`}
        visible={!!kindPicker}
        okText="Add to Smart POS"
        onOk={() => { setSmartDevice(kindPicker.tag, kindPicker.kind); setKindPicker(null); }}
        onCancel={() => setKindPicker(null)}
        destroyOnClose
      >
        <Radio.Group
          value={kindPicker?.kind}
          onChange={(e) => setKindPicker((prev) => ({ ...prev, kind: e.target.value }))}
        >
          <Space direction="vertical">
          {DEVICE_KIND_CHOICES.map(([value, label]) => (
            <Radio key={value} value={value}>{label}</Radio>
          ))}
          </Space>
        </Radio.Group>
      </Modal>
      <Input.Search
        placeholder={`Search ${(data || []).length} tags…`}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        style={{ maxWidth: '360px', marginBottom: '12px' }}
        allowClear
      />
      <Table
        rowKey="id"
        dataSource={rows}
        columns={columns}
        pagination={rows.length > 25 ? { pageSize: 25 } : false}
        size="middle"
      />
    </div>
  );
};

export default StrikeTagsListView;
